import { provideApollo } from 'apollo-angular';
import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client/cache';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloLink, split } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { contentGlobalErrors } from '../../content/content.error';
import { pageWithoutLayout } from '../../../app.utils';
import { selectedFiscalYearId } from '../../../store/organization.global-store';
import { selectedOrganizationId } from '../../../store/current-user-id';
import {
    isWebsocketRestartEvent,
    registerWebsocketClient,
    wsConnectionLost$,
} from './websocket-connection';
import { map } from 'rxjs';

declare module '@apollo/client' {
    export namespace ApolloClient {
        export namespace DeclareDefaultOptions {
            interface Query {
                errorPolicy?: 'none' | 'all' | 'ignore';
            }
        }
    }
}

const GRAPHQL_ENDPOINT = '/graphql';

export const apolloLinkProvider = provideApollo(() => {
    const auth0Link = new ApolloLink((operation, forward) => {
        operation.setContext({
            // more modern syntax once batching link gets updated
            // headers: new HttpHeaders().set(
            //     'Authorization',
            //     `Bearer ${token}`,
            // ),
            headers: {
                // handle on gateway? use store?
                selectedOrganizationId: selectedOrganizationId() || '',
                selectedFiscalYearId: selectedFiscalYearId() || '',
                ts: new Date().getTime(),
            },
        });
        return forward(operation);
    });

    const batch = auth0Link.concat(
        new BatchHttpLink({ uri: GRAPHQL_ENDPOINT }),
    );

    const hostName = window.location.host;
    const isLocalhost = hostName === 'localhost:4200';
    const isSecure = window.location.protocol === 'https:';
    const wsProtocol = isSecure ? 'wss' : 'ws';
    const websocketClient = createClient({
        url: `${wsProtocol}://${
            isLocalhost ? 'localhost:1357' : hostName
        }${GRAPHQL_ENDPOINT}`,
        retryAttempts: Infinity,
        retryWait: async (retries) => {
            await new Promise((res) =>
                setTimeout(res, Math.min(1000 * retries, 5000)),
            );
        },
        shouldRetry: () => true,
        keepAlive: 10_000,
    });
    registerWebsocketClient(websocketClient);
    websocketClient.on('closed', (event) => {
        if (!isWebsocketRestartEvent(event)) wsConnectionLost$.next();
    });
    const ws = new GraphQLWsLink(websocketClient);

    const link = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
            );
        },
        ws,
        batch,
    );

    const apolloLinkWithAuth = onError(
        ({ error, operation }) => {
            const suppressGlobalErrors = operation.getContext()['suppressGlobalErrors'] === true;
            if (CombinedGraphQLErrors.is(error)) {
                const msg = error.errors[0]?.message || '';
                if (msg === 'Unauthorized') {
                    if (!pageWithoutLayout(window.location.href)) {
                        window.location.href = 'login';
                    }
                    // This error is shown under login form - otherwise it throws at whoami req to check if user was kept logged on
                    return;
                } else if (msg === 'Forbidden') {
                    // Just ignore it - unsolved issue when login as different user to unaccessible organization
                    // Would require to first solve all those Observable bullshits regarding selectedOrganizationId
                    return;
                }
                if (suppressGlobalErrors) return;
                contentGlobalErrors.next(msg);
            } else if (!suppressGlobalErrors && ServerError.is(error)) {
                contentGlobalErrors.next(error.message);
            } else if (!suppressGlobalErrors) {
                contentGlobalErrors.next(error.message);
            }
        },
    ).concat(link);

    const highLevelObjectStrategy = {
        keyArgs: false,
        merge(
            existing: unknown,
            incoming: unknown,
            { mergeObjects }: { mergeObjects: any },
        ) {
            return mergeObjects(existing, incoming);
        },
    } as const;

    const afterwareLink = new ApolloLink((operation, forward) => {
        return forward(operation).pipe(map(response => {
            const context = operation.getContext();
            const headers = context?.response?.headers;
            if ((response as any).data) {
                (response as any).data.__ts = headers?.get?.('ts') || -1;
            }

            return response;
        }));
    });

    return {
        cache: new InMemoryCache({
            dataIdFromObject(responseObject) {
                switch (responseObject.__typename) {
                    case 'CollectionStatisticsObject':
                        return `CollectionStatisticsObject:${responseObject['id']}${responseObject['donationFormId']}${responseObject['from']}`;
                    case 'PersonObject':
                        return responseObject['relationId']
                            ? `Person:${responseObject['relationId']}`
                            : `Person:${responseObject['id']}`;
                    default:
                        return defaultDataIdFromObject(responseObject);
                }
            },
            typePolicies: {
                Query: {
                    fields: {
                        authentication: highLevelObjectStrategy,
                        center: highLevelObjectStrategy,
                        compensation: highLevelObjectStrategy,
                        donation: highLevelObjectStrategy,
                        donationReport: highLevelObjectStrategy,
                        donor: highLevelObjectStrategy,
                        donorActivity: highLevelObjectStrategy,
                        employmentType: highLevelObjectStrategy,
                        epicrisis: highLevelObjectStrategy,
                        fileCategory: highLevelObjectStrategy,
                        folder: highLevelObjectStrategy,
                        folderMedication: highLevelObjectStrategy,
                        form: highLevelObjectStrategy,
                        group: highLevelObjectStrategy,
                        medicalProcedure: highLevelObjectStrategy,
                        medication: highLevelObjectStrategy,
                        notifications: highLevelObjectStrategy,
                        organization: highLevelObjectStrategy,
                        organizationPerson: highLevelObjectStrategy,
                        person: highLevelObjectStrategy,
                        personContact: highLevelObjectStrategy,
                        rule: highLevelObjectStrategy,
                        service: highLevelObjectStrategy,
                        serviceRecord: highLevelObjectStrategy,
                        systemSettings: highLevelObjectStrategy,
                        template: highLevelObjectStrategy,
                        user: highLevelObjectStrategy,
                        userTable: highLevelObjectStrategy,
                        visitHistory: highLevelObjectStrategy,
                        voucher: highLevelObjectStrategy,
                        work: highLevelObjectStrategy,
                    },
                },
            },
        }),
        link: afterwareLink.concat(apolloLinkWithAuth),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'network-only',
            },
            query: {
                fetchPolicy: 'no-cache',
                errorPolicy: 'all',
            },
        },
    };
});
